import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';

import type { NormalizedDateFilter } from '@/components/DatePicker/RichDatePicker';
import { useDateRangeFilter } from '@/hooks/useDateRangeFilter';
import { usePaginationSearch } from '@/lib/listPage';
import { OrderDealTable } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/order/-components/OrderDealTable';
import { OrderSearchFilterBar } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/order/-components/OrderSearchFilterBar';
import { useOrderDealList } from '@/routes/_staff/software/_layout/contract-and-cost/_contract-and-cost/deal/_deal/order/-query';

const WITHOUT_CANCELED = ['Ordered', 'Draft'] as const;
const ALL_STATUSES = ['Ordered', 'Draft', 'Canceled'] as const;

const SearchSchema = z.object({
  pageIndex: z.number().default(0),
  pageSize: z.number().default(100),
  freeText: z.string().optional(),
  showCanceled: z.boolean().default(true),
  orderDateFrom: z.string().optional(),
  orderDateTo: z.string().optional(),
});

export const Route = createFileRoute(
  '/_staff/software/$id/_$id/contract-and-cost/_contract-and-cost/deal/_deal/order/',
)({
  component: OrderTab,
  validateSearch: zodValidator(SearchSchema),
});

function OrderTab() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [pagination, setPagination] = usePaginationSearch({
    search,
    onSearchChange: (s) => navigate({ search: s }),
  });

  const statuses = useMemo(
    () => (search.showCanceled ? [...ALL_STATUSES] : [...WITHOUT_CANCELED]),
    [search.showCanceled],
  );

  const { data, isPending } = useOrderDealList({
    page: search.pageIndex + 1,
    pageSize: search.pageSize,
    freeText: search.freeText,
    statuses,
  });

  const {
    currentFilter: orderDateFilter,
    handleFilterChange: handleOrderDateQueryChange,
  } = useDateRangeFilter(search.orderDateFrom, search.orderDateTo);

  const handleOrderDateFilterChange = useCallback(
    ({ normalized }: { normalized?: NormalizedDateFilter }) => {
      const { fromDate, toDate } = handleOrderDateQueryChange(normalized);
      navigate({
        search: (prev) => ({
          ...prev,
          orderDateFrom: fromDate,
          orderDateTo: toDate,
          pageIndex: 0,
        }),
      });
    },
    [navigate, handleOrderDateQueryChange],
  );

  const handleShowCanceledChange = useCallback(
    (checked: boolean) => {
      navigate({
        search: (prev) => ({
          ...prev,
          showCanceled: checked,
          pageIndex: 0,
        }),
      });
    },
    [navigate],
  );

  return (
    <>
      <OrderSearchFilterBar
        searchValue={search.freeText ?? ''}
        onSearchChange={(e) =>
          navigate({
            search: (prev) => ({
              ...prev,
              freeText: e.target.value || undefined,
              pageIndex: 0,
            }),
          })
        }
        orderDateFilter={orderDateFilter}
        onOrderDateFilterChange={handleOrderDateFilterChange}
        showCanceled={search.showCanceled}
        onShowCanceledChange={handleShowCanceledChange}
        onExport={() => {}}
      />
      <OrderDealTable
        data={data?.items ?? []}
        isPending={isPending}
        totalCount={data?.count}
        pageCount={data?.maxPage}
        pagination={pagination}
        onPaginationChange={setPagination}
        hideSoftwareColumn
        className="flex-1 min-h-0"
      />
    </>
  );
}
